import * as React from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";

import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const http = axios.create({
  baseURL: "http://127.0.0.1:8000",
  timeout: 20000,
});

const API = {
  fluidHistory: "/api/medcharts/fluid-therapy-patient-history/?format=json",
  medicationHistory: "/api/medcharts/medication-patient-history/?format=json",
  investigationHistory: "/api/investigations/patient-history/",
};

/* -------------------------------------------------------------------------- */
/*                                  ANIMATION                                 */
/* -------------------------------------------------------------------------- */

const softFloat = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const pulseRing = keyframes`
  0% { transform: scale(0.96); opacity: 0.65; }
  50% { transform: scale(1.03); opacity: 1; }
  100% { transform: scale(0.96); opacity: 0.65; }
`;

/* -------------------------------------------------------------------------- */
/*                               HELPER FUNCTIONS                             */
/* -------------------------------------------------------------------------- */

function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function fmt(value) {
  if (value === undefined || value === null || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
}

function getFluidLabel(fluid) {
  const map = {
    NS: "Normal Saline",
    RL: "Ringer Lactate",
    DNS: "Dextrose Normal Saline",
    D5: "5% Dextrose",
    D10: "10% Dextrose",
    OTHER: "Other",
  };
  return map[fluid] || fluid || "—";
}

function getMedicationTypeLabel(type) {
  const map = {
    ANTIBIOTIC: "Antibiotic",
    ANALGESIC: "Analgesic",
    ANTIPYRETIC: "Antipyretic",
    ANTACID: "Antacid",
    ANTIHYPERTENSIVE: "Antihypertensive",
    ANTIDIABETIC: "Antidiabetic",
    INSULIN: "Insulin",
    IV_FLUID: "IV Fluid",
    VITAMIN: "Vitamin",
    OTHER: "Other",
  };
  return map[type] || type || "—";
}

function getRouteLabel(route) {
  const map = {
    ORAL: "Oral",
    IV: "IV",
    IM: "IM",
    SC: "SC",
    TOPICAL: "Topical",
    INHALATION: "Inhalation",
    RECTAL: "Rectal",
    NASAL: "Nasal",
    OTHER: "Other",
  };
  return map[route] || route || "—";
}

function getFrequencyLabel(frequency) {
  const map = {
    OD: "OD - Once Daily",
    BD: "BD - Twice Daily",
    TDS: "TDS - Three Times Daily",
    QID: "QID - Four Times Daily",
    SOS: "SOS - As Needed",
    STAT: "STAT - Immediately",
    Q4H: "Every 4 Hours",
    Q6H: "Every 6 Hours",
    Q8H: "Every 8 Hours",
    Q12H: "Every 12 Hours",
  };
  return map[frequency] || frequency || "—";
}

function getStatusColor(status) {
  const v = String(status || "").toLowerCase();
  if (v.includes("completed")) return Colors.status.success;
  if (v.includes("pending")) return Colors.status.warning;
  if (v.includes("ordered")) return Colors.status.info;
  return Colors.brand.primary;
}

function getAbnormalFlagColor(flag) {
  const v = String(flag || "").toLowerCase();
  if (v.includes("high") || v.includes("positive") || v.includes("abnormal") || v.includes("critical")) {
    return Colors.status.danger;
  }
  if (v.includes("low") || v.includes("borderline")) {
    return Colors.status.warning;
  }
  if (v.includes("normal") || v.includes("negative")) {
    return Colors.status.success;
  }
  return Colors.brand.primary;
}

/* -------------------------------------------------------------------------- */
/*                            REUSABLE UI PARTS                               */
/* -------------------------------------------------------------------------- */

function Panel({ title, icon, right, children, sticky = false, sx = {} }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
        background: Colors.bg.paper,
        overflow: "hidden",
        ...(sticky
          ? {
              position: "sticky",
              top: 10,
              zIndex: 10,
            }
          : {}),
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 1.8 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography
              sx={{
                fontSize: { xs: 15, md: 17 },
                fontWeight: 900,
                color: Colors.text.title,
              }}
            >
              {title}
            </Typography>
          </Stack>
          {right}
        </Stack>

        <Divider sx={{ borderColor: Colors.border.light, mb: 1.3 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function DetailLine({ label, value }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        py: 0.55,
        borderBottom: `1px solid ${Colors.border.topLine}`,
      }}
    >
      <Typography
        sx={{
          width: 104,
          minWidth: 104,
          fontSize: 11,
          fontWeight: 800,
          color: Colors.text.muted,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: Colors.text.body,
          wordBreak: "break-word",
          lineHeight: 1.3,
        }}
      >
        {fmt(value)}
      </Typography>
    </Stack>
  );
}

function SummaryCountGroup({ fluidCount, medicationCount, investigationCount }) {
  const items = [
    {
      label: "Fluid Therapy Records",
      value: fluidCount,
      color: Colors.status.success,
      icon: <WaterDropRoundedIcon sx={{ color: Colors.status.success, fontSize: 18 }} />,
    },
    {
      label: "Medication Records",
      value: medicationCount,
      color: Colors.status.info,
      icon: <MedicationOutlinedIcon sx={{ color: Colors.status.info, fontSize: 18 }} />,
    },
    {
      label: "Investigation Records",
      value: investigationCount,
      color: Colors.status.warning,
      icon: <ScienceRoundedIcon sx={{ color: Colors.status.warning, fontSize: 18 }} />,
    },
  ];

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
        background: Colors.bg.paper,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.4 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 900,
            color: Colors.text.title,
            mb: 1.2,
          }}
        >
          Clinical Records Summary
        </Typography>

        <Stack spacing={1}>
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 1.2,
                borderRadius: 2.2,
                border: `1px solid ${alpha(item.color, 0.22)}`,
                bgcolor: alpha(item.color, 0.07),
                animation: `${softFloat} ${3 + index * 0.2}s ease-in-out infinite`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(item.color, 0.14),
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: Colors.text.body,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: Colors.text.title,
                    lineHeight: 1,
                  }}
                >
                  {fmt(item.value)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SummarySingleCard({ label, value, color }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        boxShadow: Colors.shadow.brand,
        background: Colors.bg.paper,
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: 1.4,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            p: 1.5,
            borderRadius: 2.5,
            border: `1px solid ${alpha(color, 0.22)}`,
            bgcolor: alpha(color, 0.08),
            textAlign: "center",
            animation: `${softFloat} 3.2s ease-in-out infinite`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: Colors.text.muted, fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              fontSize: 22,
              fontWeight: 900,
              color: Colors.text.title,
              wordBreak: "break-word",
            }}
          >
            {fmt(value)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function CircularOnlyHistoryChart({ fluidCount = 0, medicationCount = 0, investigationCount = 0 }) {
  const values = [
    { label: "Fluid", value: Number(fluidCount || 0), color: Colors.status.success },
    { label: "Medicine", value: Number(medicationCount || 0), color: Colors.status.info },
    { label: "Investigation", value: Number(investigationCount || 0), color: Colors.status.warning },
  ];

  const total = values.reduce((sum, item) => sum + item.value, 0);
  const fluidDeg = total ? (values[0].value / total) * 360 : 0;
  const medDeg = total ? ((values[0].value + values[1].value) / total) * 360 : 0;

  return (
    <Box
      sx={{
        p: { xs: 1.2, md: 1.8 },
        borderRadius: 3,
        border: `1px solid ${Colors.border.light}`,
        bgcolor: alpha(Colors.brand.primary, 0.02),
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              width: { xs: 180, md: 220 },
              height: { xs: 180, md: 220 },
              mx: "auto",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              position: "relative",
              background: `conic-gradient(
                ${Colors.status.success} 0deg ${fluidDeg}deg,
                ${Colors.status.info} ${fluidDeg}deg ${medDeg}deg,
                ${Colors.status.warning} ${medDeg}deg 360deg
              )`,
              animation: `${pulseRing} 2.8s ease-in-out infinite`,
              boxShadow: `0 16px 36px ${alpha(Colors.brand.primary, 0.12)}`,
            }}
          >
            <Box
              sx={{
                width: { xs: 110, md: 130 },
                height: { xs: 110, md: 130 },
                borderRadius: "50%",
                bgcolor: Colors.bg.paper,
                display: "grid",
                placeItems: "center",
                border: `1px solid ${Colors.border.light}`,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: { xs: 28, md: 32 },
                    fontWeight: 900,
                    color: Colors.text.title,
                    lineHeight: 1,
                  }}
                >
                  {total}
                </Typography>
                <Typography sx={{ fontSize: 11, color: Colors.text.muted, fontWeight: 800 }}>
                  Total Records
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography
            sx={{
              fontSize: 13,
              color: Colors.text.muted,
              fontWeight: 800,
              mb: 1.4,
            }}
          >
            Clinical Records Distribution
          </Typography>

          <Stack spacing={1}>
            {values.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.1,
                  borderRadius: 2,
                  border: `1px solid ${Colors.border.light}`,
                  bgcolor: Colors.bg.paper,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: item.color,
                      }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: Colors.text.body }}>
                      {item.label}
                    </Typography>
                  </Stack>

                  <Typography sx={{ fontSize: 16, fontWeight: 900, color: Colors.text.title }}>
                    {item.value}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

function MobileHistoryCards({ rows, type }) {
  if (!rows.length) {
    return <Alert severity="info">No records found.</Alert>;
  }

  return (
    <Stack spacing={1}>
      {rows.map((row, index) => (
        <Accordion
          key={row.id || index}
          disableGutters
          sx={{
            borderRadius: "14px !important",
            overflow: "hidden",
            border: `1px solid ${Colors.border.light}`,
            boxShadow: "none",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 900, color: Colors.text.title }}>
                {type === "fluid" && (row.fluid_label || getFluidLabel(row.fluid))}
                {type === "medication" && (row.type_label || getMedicationTypeLabel(row.type))}
                {type === "investigation" && (row.test_name || "Investigation")}
              </Typography>
              <Typography sx={{ fontSize: 12, color: Colors.text.muted, mt: 0.2 }}>
                {type === "fluid" && `Created: ${formatDateTime(row.created_at)}`}
                {type === "medication" && `Dose: ${fmt(row.dose)} | Duration: ${fmt(row.duration)}`}
                {type === "investigation" &&
                  `Date: ${formatDate(row.date)} | Status: ${fmt(row.status_label || row.status)}`}
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={0.5}>
              {type === "fluid" && (
                <>
                  <DetailLine label="Fluid" value={row.fluid_label || getFluidLabel(row.fluid)} />
                  <DetailLine label="Qty" value={row.qty_ml} />
                  <DetailLine label="Speed" value={row.speed_ml_hr} />
                  <DetailLine label="Frequency" value={row.frequency} />
                  <DetailLine label="Duration" value={row.duration} />
                  <DetailLine label="Created" value={formatDateTime(row.created_at)} />
                </>
              )}

              {type === "medication" && (
                <>
                  <DetailLine label="Type" value={row.type_label || getMedicationTypeLabel(row.type)} />
                  <DetailLine label="Generic" value={row.generic} />
                  <DetailLine label="Brand" value={row.brand} />
                  <DetailLine label="Route" value={row.route_label || getRouteLabel(row.route)} />
                  <DetailLine label="Dose" value={row.dose} />
                  <DetailLine label="Frequency" value={row.frequency_label || getFrequencyLabel(row.frequency)} />
                  <DetailLine label="Duration" value={row.duration} />
                </>
              )}

              {type === "investigation" && (
                <>
                  <DetailLine label="Category" value={row.category_label || row.category} />
                  <DetailLine label="Test Name" value={row.test_name} />
                  <DetailLine label="Body Part" value={row.body_part_sample} />
                  <DetailLine label="Result" value={row.result} />
                  <DetailLine label="Impression" value={row.impression_diagnosis} />
                  <DetailLine label="Status" value={row.status_label || row.status} />
                  <DetailLine label="Date" value={formatDate(row.date)} />
                  <DetailLine label="Remarks" value={row.remarks} />
                  <DetailLine label="Unit" value={row.unit} />
                  <DetailLine label="Normal Range" value={row.normal_range} />
                  <DetailLine label="Abnormal Flag" value={row.abnormal_flag_label || row.abnormal_flag} />
                  <DetailLine label="Ordered By" value={row.ordered_by} />
                  <DetailLine label="Performed By" value={row.performed_by} />
                  <DetailLine label="Created" value={formatDateTime(row.created_at)} />
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

function DesktopTable({ columns, rows, emptyText }) {
  if (!rows.length) {
    return <Alert severity="info">{emptyText}</Alert>;
  }

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        border: `1px solid ${Colors.border.light}`,
        borderRadius: 2.5,
        background: Colors.bg.paper,
      }}
    >
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
        <Box component="thead">
          <Box component="tr" sx={{ background: Colors.bg.hover }}>
            {columns.map((col) => (
              <Box
                key={col.key}
                component="th"
                sx={{
                  textAlign: "left",
                  p: 1.5,
                  fontSize: 13,
                  fontWeight: 900,
                  color: Colors.text.header,
                  borderBottom: `1px solid ${Colors.border.light}`,
                  whiteSpace: "nowrap",
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {rows.map((row, idx) => (
            <Box
              component="tr"
              key={row.id || idx}
              sx={{
                "&:hover": {
                  background: alpha(Colors.brand.primary, 0.02),
                },
              }}
            >
              {columns.map((col) => (
                <Box
                  key={col.key}
                  component="td"
                  sx={{
                    p: 1.5,
                    fontSize: 14,
                    color: Colors.text.body,
                    borderBottom: `1px solid ${Colors.border.light}`,
                    whiteSpace: "nowrap",
                    verticalAlign: "top",
                  }}
                >
                  {typeof col.render === "function" ? col.render(row) : fmt(row[col.key])}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 MAIN PAGE                                  */
/* -------------------------------------------------------------------------- */

export default function PatientClinicalHistoryDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientUhid } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const passedPatient = location.state?.patient || null;

  const [selectedPatient, setSelectedPatient] = React.useState(passedPatient);
  const [fluidHistory, setFluidHistory] = React.useState([]);
  const [medicationHistory, setMedicationHistory] = React.useState([]);
  const [investigationHistory, setInvestigationHistory] = React.useState([]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [tabValue, setTabValue] = React.useState(0);

  const fetchAllHistory = React.useCallback(async (uhid) => {
    try {
      setLoading(true);
      setError("");

      const encoded = encodeURIComponent(uhid);

      const [fluidRes, medicationRes, investigationRes] = await Promise.all([
        http.get(`${API.fluidHistory}&patient_uhid=${encoded}`),
        http.get(`${API.medicationHistory}&patient_uhid=${encoded}`),
        http.get(`${API.investigationHistory}?patient_uhid=${encoded}`),
      ]);

      const fluidRows = toArray(fluidRes.data);
      const medicationRows = toArray(medicationRes.data);
      const investigationRows = toArray(investigationRes.data);

      setFluidHistory(fluidRows);
      setMedicationHistory(medicationRows);
      setInvestigationHistory(investigationRows);

      const firstFluid = fluidRows[0] || {};
      const firstMed = medicationRows[0] || {};
      const firstInv = investigationRows[0] || {};

      setSelectedPatient((prev) => ({
        ...prev,
        patient_uhid:
          prev?.patient_uhid ||
          firstFluid.patient_uhid ||
          firstMed.patient_uhid ||
          firstInv.patient_uhid ||
          uhid,
        patient_name:
          prev?.patient_name ||
          firstFluid.patient_name ||
          firstMed.patient_name ||
          firstInv.patient_name ||
          "",
        admission_number:
          prev?.admission_number ||
          firstFluid.admission_number ||
          firstMed.admission_number ||
          firstInv.admission_number ||
          "",
        doctor_name:
          prev?.doctor_name ||
          firstFluid.doctor_name ||
          firstMed.doctor_name ||
          firstInv.doctor_name ||
          "",
        total_fluid_times: fluidRows.length,
        total_medications: medicationRows.length,
        total_investigations: investigationRows.length,
        last_fluid_at: firstFluid.created_at || "",
        last_medication_at: firstMed.created_at || "",
        last_investigation_at: firstInv.created_at || "",
      }));
    } catch (err) {
      console.error("History load error:", err);
      setError("Unable to load patient full history.");
      setFluidHistory([]);
      setMedicationHistory([]);
      setInvestigationHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (patientUhid) {
      fetchAllHistory(patientUhid);
    }
  }, [patientUhid, fetchAllHistory]);

  const fluidColumns = [
    {
      key: "fluid",
      label: "Fluid",
      render: (row) => row.fluid_label || getFluidLabel(row.fluid),
    },
    { key: "qty_ml", label: "Qty (ml)" },
    { key: "speed_ml_hr", label: "Speed (ml/hr)" },
    { key: "frequency", label: "Frequency" },
    { key: "duration", label: "Duration" },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatDateTime(row.created_at),
    },
  ];

  const medicationColumns = [
    {
      key: "type",
      label: "Type",
      render: (row) => row.type_label || getMedicationTypeLabel(row.type),
    },
    { key: "generic", label: "Generic" },
    { key: "brand", label: "Brand" },
    {
      key: "route",
      label: "Route",
      render: (row) => row.route_label || getRouteLabel(row.route),
    },
    { key: "dose", label: "Dose" },
    {
      key: "frequency",
      label: "Frequency",
      render: (row) => row.frequency_label || getFrequencyLabel(row.frequency),
    },
    { key: "duration", label: "Duration" },
  ];

  const investigationColumns = [
    {
      key: "category",
      label: "Category",
      render: (row) => fmt(row.category_label || row.category),
    },
    { key: "test_name", label: "Test Name" },
    { key: "body_part_sample", label: "Body Part / Sample" },
    { key: "result", label: "Result" },
    { key: "impression_diagnosis", label: "Impression / Diagnosis" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <Chip
          size="small"
          label={fmt(row.status_label || row.status)}
          sx={{
            bgcolor: alpha(getStatusColor(row.status_label || row.status), 0.14),
            color: Colors.text.body,
            fontWeight: 800,
          }}
        />
      ),
    },
    {
      key: "date",
      label: "Date",
      render: (row) => formatDate(row.date),
    },
    { key: "remarks", label: "Remarks" },
    { key: "unit", label: "Unit" },
    { key: "normal_range", label: "Normal Range" },
    {
      key: "abnormal_flag",
      label: "Abnormal Flag",
      render: (row) => (
        <Chip
          size="small"
          label={fmt(row.abnormal_flag_label || row.abnormal_flag)}
          sx={{
            bgcolor: alpha(getAbnormalFlagColor(row.abnormal_flag_label || row.abnormal_flag), 0.14),
            color: Colors.text.body,
            fontWeight: 800,
          }}
        />
      ),
    },
    { key: "ordered_by", label: "Ordered By" },
    { key: "performed_by", label: "Performed By" },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatDateTime(row.created_at),
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: Colors.bg.app, p: { xs: 1.2, md: 1.8 } }}>
      <Stack spacing={1.5}>
        <Panel
          title="PATIENT FULL HISTORY DETAILS"
          icon={<AssignmentRoundedIcon sx={{ color: Colors.text.icon }} />}
          right={
            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate(ROUTES.PatientClinicalOverviewDashboard)}
              sx={{
                borderRadius: 2.2,
                textTransform: "none",
                fontWeight: 800,
                color: Colors.text.body,
                borderColor: Colors.border.input,
              }}
            >
              Back
            </Button>
          }
          sticky
        >
          <Typography sx={{ fontSize: 13, color: Colors.text.muted }}>
            Full patient details and complete history are shown on this page after clicking View History.
          </Typography>
        </Panel>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Card
            sx={{
              borderRadius: 3,
              border: `1px solid ${Colors.border.light}`,
              boxShadow: Colors.shadow.brand,
            }}
          >
            <CardContent sx={{ py: 7, textAlign: "center" }}>
              <CircularProgress sx={{ color: Colors.brand.primary }} />
              <Typography sx={{ mt: 1.5, color: Colors.text.title, fontWeight: 900 }}>
                Loading full patient history...
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* PATIENT DETAILS */}
            <Grid container spacing={1.2}>
              <Grid item xs={12} lg={7}>
                <Panel
                  title="Patient Details"
                  icon={<PersonRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.2 }}>
                    <Avatar
                      sx={{
                        width: 68,
                        height: 68,
                        bgcolor: alpha(Colors.brand.primary, 0.12),
                        color: Colors.brand.primary,
                        fontSize: 28,
                        fontWeight: 900,
                      }}
                    >
                      {String(selectedPatient?.patient_name || "P").charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 20, md: 25 },
                          fontWeight: 900,
                          color: Colors.text.title,
                          wordBreak: "break-word",
                        }}
                      >
                        {fmt(selectedPatient?.patient_name)}
                      </Typography>
                      <Typography sx={{ mt: 0.3, fontSize: 13, color: Colors.text.muted }}>
                        UHID: {fmt(selectedPatient?.patient_uhid)}
                      </Typography>
                      <Typography sx={{ mt: 0.15, fontSize: 13, color: Colors.text.muted }}>
                        Admission No: {fmt(selectedPatient?.admission_number)}
                      </Typography>
                    </Box>
                  </Stack>

                  <DetailLine label="Patient Name" value={selectedPatient?.patient_name} />
                  <DetailLine label="UHID" value={selectedPatient?.patient_uhid} />
                  <DetailLine label="Admission No" value={selectedPatient?.admission_number} />
                  <DetailLine label="Doctor Name" value={selectedPatient?.doctor_name} />
                </Panel>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Panel
                  title="Doctor & Admission Details"
                  icon={<LocalHospitalRoundedIcon sx={{ color: Colors.text.icon }} />}
                >
                  <DetailLine label="Doctor" value={selectedPatient?.doctor_name} />
                  <DetailLine label="Admission" value={selectedPatient?.admission_number} />
                  <DetailLine label="Last Fluid" value={formatDateTime(selectedPatient?.last_fluid_at)} />
                  <DetailLine label="Last Medicine" value={formatDateTime(selectedPatient?.last_medication_at)} />
                  <DetailLine label="Last Test" value={formatDateTime(selectedPatient?.last_investigation_at)} />
                </Panel>
                
              </Grid>
              <Grid item xs={12} md={8}>
                <SummaryCountGroup
                  fluidCount={selectedPatient?.total_fluid_times || 0}
                  medicationCount={selectedPatient?.total_medications || 0}
                  investigationCount={selectedPatient?.total_investigations || 0}
                />
              </Grid>
              
            </Grid>

            {/* MOVED SUMMARY SECTION AFTER DOCTOR & ADMISSION DETAILS */}
          
            {/* CIRCULAR ONLY CHART */}
            <Panel
              title="Animated Clinical Chart"
              icon={<TimelineRoundedIcon sx={{ color: Colors.text.icon }} />}
            >
              <CircularOnlyHistoryChart
                fluidCount={selectedPatient?.total_fluid_times || 0}
                medicationCount={selectedPatient?.total_medications || 0}
                investigationCount={selectedPatient?.total_investigations || 0}
              />
            </Panel>

            {/* TABS */}
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${Colors.border.light}`,
                boxShadow: Colors.shadow.brand,
                background: Colors.bg.paper,
              }}
            >
              <CardContent sx={{ p: { xs: 1, md: 1.3 } }}>
                <Tabs
                  value={tabValue}
                  onChange={(_, value) => setTabValue(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    mb: 1.3,
                    "& .MuiTabs-indicator": {
                      backgroundColor: Colors.brand.primary,
                      height: 4,
                      borderRadius: 999,
                    },
                  }}
                >
                  <Tab
                    icon={<WaterDropRoundedIcon />}
                    iconPosition="start"
                    label="Fluid Therapy History"
                    sx={{ textTransform: "none", fontWeight: 900 }}
                  />
                  <Tab
                    icon={<MedicationOutlinedIcon />}
                    iconPosition="start"
                    label="Medication History"
                    sx={{ textTransform: "none", fontWeight: 900 }}
                  />
                  <Tab
                    icon={<ScienceRoundedIcon />}
                    iconPosition="start"
                    label="Investigation History"
                    sx={{ textTransform: "none", fontWeight: 900 }}
                  />
                </Tabs>

                {tabValue === 0 && (
                  <Panel
                    title="Fluid Therapy History"
                    icon={<WaterDropRoundedIcon sx={{ color: Colors.status.success }} />}
                    right={
                      <Chip
                        label={`${fluidHistory.length} Record(s)`}
                        sx={{
                          bgcolor: alpha(Colors.status.success, 0.14),
                          color: Colors.text.body,
                          fontWeight: 900,
                        }}
                      />
                    }
                  >
                    {isMobile ? (
                      <MobileHistoryCards rows={fluidHistory} type="fluid" />
                    ) : (
                      <DesktopTable
                        rows={fluidHistory}
                        columns={fluidColumns}
                        emptyText="No fluid therapy history found."
                      />
                    )}
                  </Panel>
                )}

                {tabValue === 1 && (
                  <Panel
                    title="Medication History"
                    icon={<MedicationOutlinedIcon sx={{ color: Colors.status.info }} />}
                    right={
                      <Chip
                        label={`${medicationHistory.length} Record(s)`}
                        sx={{
                          bgcolor: alpha(Colors.status.info, 0.14),
                          color: Colors.text.body,
                          fontWeight: 900,
                        }}
                      />
                    }
                  >
                    {isMobile ? (
                      <MobileHistoryCards rows={medicationHistory} type="medication" />
                    ) : (
                      <DesktopTable
                        rows={medicationHistory}
                        columns={medicationColumns}
                        emptyText="No medication history found."
                      />
                    )}
                  </Panel>
                )}

                {tabValue === 2 && (
                  <Panel
                    title="Investigation History"
                    icon={<ScienceRoundedIcon sx={{ color: Colors.status.warning }} />}
                    right={
                      <Chip
                        label={`${investigationHistory.length} Record(s)`}
                        sx={{
                          bgcolor: alpha(Colors.status.warning, 0.18),
                          color: Colors.text.body,
                          fontWeight: 900,
                        }}
                      />
                    }
                  >
                    {isMobile ? (
                      <MobileHistoryCards rows={investigationHistory} type="investigation" />
                    ) : (
                      <DesktopTable
                        rows={investigationHistory}
                        columns={investigationColumns}
                        emptyText="No investigation history found."
                      />
                    )}
                  </Panel>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Box>
  );
}